import { 
  Component, 
  OnInit,
  OnDestroy,
  OnChanges,
  Input,
  SimpleChanges,
  Output,
  EventEmitter,
  Inject,
  TemplateRef,
  forwardRef
} from '@angular/core';

import { isEqual, isNil } from 'lodash-es';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { StepConfig } from './components/wizard.component';
import { BaseComponent } from './components/base.component';

import { SharedModule } from './shared.module';

import { UtilsService } from './utils.service';
import { SettingsValuesService } from './settings.values.service';
import { FootprintManager_ShellService } from './FootprintManager.shell.service';
import { FootprintManager_OperationService } from './FootprintManager.operation.service';
import { FootprintManager_DatasourceService } from './FootprintManager.datasource.index';
import { FootprintManager_FlowService } from './FootprintManager.flow.index';
import { FootprintManager_ReportService } from './FootprintManager.report.index';
import { FootprintManager_LocalizationService } from './FootprintManager.localization.service';
import { FootprintManager_pack_verification_wizard_ComponentContextService } from './FootprintManager.pack_verification_wizard.component.context.service';
import { Language } from './localization.service';
import { JobStatus } from './common-interfaces'
import { ApplicationType, ComponentType } from './app-context.service';
import { CleanupLoggerService } from './cleanup.logging.service';
import { $frontendTypes} from './FootprintManager.frontend.types'
import { $frontendTypes as $types} from './FootprintManager.frontend.types' 

import { EModalSize, EToasterType, EToasterPosition } from 'wavelength-ui';

import { FootprintManager_pack_verification_verify_container_editorComponent } from './FootprintManager.pack_verification_verify_container_editor.component';
import { FootprintManager_pack_verification_shipping_container_properties_editorComponent } from './FootprintManager.pack_verification_shipping_container_properties_editor.component';
import { FootprintManager_accessorial_tasks_gridComponent } from './FootprintManager.accessorial_tasks_grid.component';
import { FootprintManager_pack_verification_completion_editorComponent } from './FootprintManager.pack_verification_completion_editor.component';


@Component({
  standalone: true,
  imports: [
    SharedModule,

    forwardRef(() => FootprintManager_pack_verification_verify_container_editorComponent),
    forwardRef(() => FootprintManager_pack_verification_shipping_container_properties_editorComponent),
    forwardRef(() => FootprintManager_accessorial_tasks_gridComponent),
    forwardRef(() => FootprintManager_pack_verification_completion_editorComponent),
  ],
  selector: 'FootprintManager-pack_verification_wizard',
  templateUrl: './FootprintManager.pack_verification_wizard.component.html'
})
export class FootprintManager_pack_verification_wizardComponent extends BaseComponent implements OnInit, OnChanges {
  _stepConfigs: StepConfig[];

  inParams: { shippingContainerId: number, startVerificationTaskId?: number, targetLocationId?: number, isBlind?: boolean } = { shippingContainerId: null, startVerificationTaskId: null, targetLocationId: null, isBlind: null };
  //#region Inputs
  @Input('shippingContainerId') set $inParams_shippingContainerId(v: number) {
    this.inParams.shippingContainerId = v;
  }
  get $inParams_shippingContainerId(): number {
    return this.inParams.shippingContainerId;
  }
  @Input('startVerificationTaskId') set $inParams_startVerificationTaskId(v: number) {
    this.inParams.startVerificationTaskId = v;
  }
  get $inParams_startVerificationTaskId(): number {
    return this.inParams.startVerificationTaskId;
  }
  @Input('targetLocationId') set $inParams_targetLocationId(v: number) {
    this.inParams.targetLocationId = v;
  }
  get $inParams_targetLocationId(): number {
    return this.inParams.targetLocationId;
  }
  @Input('isBlind') set $inParams_isBlind(v: boolean) {
    this.inParams.isBlind = v;
  }
  get $inParams_isBlind(): boolean {
    return this.inParams.isBlind;
  }
  //#endregion Inputs

  //#region Outputs
  @Output() 
  $commandsTmpRef = new EventEmitter<TemplateRef<any>>();
  @Output()
  $finish = new EventEmitter();
  //#endregion

  //#region title
  // Make it async so that it won't cause expressionChangedAfterItHasBeenCheckedError
  // The title is often meant to be shown from the parent (shell breadcrumb for example)
  // and often it will cause an expressionChangedAfterItHasBeenCheckedError because 
  // the parent has already been checked and the child now change something on the parent 
  // in dev, CD is run twice
  $titleChange = new EventEmitter<string>(true);
  private $_title: string;
  get title(): string {
    return this.$_title;
  }
  set title(t: string) {
    this.$_title = t;
    this.$titleChange.emit(this.$_title);
  }
  //#endregion title
  //#region Variables
  vars: { projectId?: number, orderId?: number, warehouseId?: number, instructions?: { entity?: string, code?: string, instruction?: string, isCompleted?: boolean, id?: number }[], properties?: { containerType?: string, dimensions?: string, weight?: string, actualWeight?: number }, canAddAccessorials?: boolean, isContentValidated?: boolean, isPropertiesValidated?: boolean, scannedValues?: { scannedValue?: string, materialId?: number, serialNumberId?: number, isVerified?: boolean, reasons?: string[], lotId?: number, packagingId?: number }[], isFailed?: boolean, processQueue?: string[], configurations?: { requireCompleteAllInstructions?: boolean, requireContainerType?: boolean, weightDiscrepancyThresholdPercent?: number, canContinueClosedSessions?: boolean, autoPrintReportsOnComplete?: boolean }, is_existing_task?: boolean, can_fail?: boolean, is_properties_initialized?: boolean } = { };
  //#endregion
  constructor(private $utils: UtilsService,
private $settings: SettingsValuesService,
private $shell: FootprintManager_ShellService,
private $datasources: FootprintManager_DatasourceService,
private $flows: FootprintManager_FlowService,
private $reports: FootprintManager_ReportService,
private $localization: FootprintManager_LocalizationService,
private $operations: FootprintManager_OperationService,
private $logger: CleanupLoggerService,
private $context: FootprintManager_pack_verification_wizard_ComponentContextService,
) { 
    super();
  }

  ngOnInit(): void {
    this.$checkRequiredInParams();
    if (!this.$hasMissingRequiredInParams) {
      this.$init();
    } else {
      this.$initEmpty();
    }
  }
  
  private $isFirstNgOnChanges = true;
  ngOnChanges(changes: SimpleChanges): void {
    if (this.$isFirstNgOnChanges) {
      this.$isFirstNgOnChanges = false;
    } else {
      this.$checkRequiredInParams();
      if(!this.$hasMissingRequiredInParams) {
        this.$init();
      } else {
        this.$initEmpty();
      }
    }
  }

  $missingRequiredInParams = [];
  get $hasMissingRequiredInParams(): boolean {
    return !!this.$missingRequiredInParams.length;
  }
  
  $checkRequiredInParams() {
    this.$missingRequiredInParams = [];
      if(isNil(this.inParams.shippingContainerId)) {
        this.$missingRequiredInParams.push('shippingContainerId');
      }
  }

  initialized = false;

  async $init() {
    this.title = 'Pack verification';
  
    const $wizard = this;
    const $utils = this.$utils;

    await this.on_init();

    this.initialized = true;
  }

  steps: {
    step1_verify_content?: {
      outParams?: { scannedValues?: { scannedValue?: string, materialId?: number, serialNumberId?: number, isVerified?: boolean, reasons?: string[], lotId?: number, packagingId?: number }[] }
    }
    step4_set_container_properties?: {
      outParams?: {  }
    }
    step6_accessorials?: {
      outParams?: {  }
    }
    step7_complete_verification?: {
      outParams?: {  }
    }
  } = { };

  stepsResultFunc(result: any) {
    this.steps = result;
  }
  
  getStepConfigs(): StepConfig[] {
    if (!this._stepConfigs) {
      const $wizard = this;
      const $utils = this.$utils;

      this._stepConfigs = [
      {
          id: 'step1_verify_content',
          title: 'Verify content',
          component: FootprintManager_pack_verification_verify_container_editorComponent,
          inParamsFunc: ($index?: number) => {
            return {
              shippingContainerId: $wizard.inParams.shippingContainerId,
              startVerificationTaskId: $wizard.inParams.startVerificationTaskId,
              isBlind: $wizard.inParams.isBlind,
              targetLocationId: $wizard.inParams.targetLocationId,
              isReadOnly: $wizard.vars.isContentValidated,
              can_fail_verification: $wizard.vars.can_fail,
            }
          },
          next: 'step4_set_container_properties',
      },
      {
          id: 'step4_set_container_properties',
          title: 'Set container properties',
          component: FootprintManager_pack_verification_shipping_container_properties_editorComponent,
          inParamsFunc: ($index?: number) => {
            return {
              shippingContainerId: $wizard.inParams.shippingContainerId,
              startVerificationTaskId: $wizard.inParams.startVerificationTaskId,
              targetLocationId: $wizard.inParams.targetLocationId,
              isReadOnly: $wizard.vars.is_existing_task || $wizard.vars.isPropertiesValidated,
              can_fail_verification: $wizard.vars.can_fail,
            }
          },
          nextConditionFunc: ($index?: number) => {
            return $wizard.vars.canAddAccessorials;
          },
          next: 'step6_accessorials',
          nextAlt: 'step7_complete_verification',
          nextButtonDisabledConditionFunc: ($index?: number) => {
            return $wizard.vars.instructions == undefined || ($wizard.vars.configurations.requireCompleteAllInstructions && $utils.isDefined($wizard.vars.instructions.find(i => !i.isCompleted))) || ($wizard.vars.configurations.requireContainerType && !$utils.isDefined($wizard.vars.properties.containerType));
          },
      },
      {
          id: 'step6_accessorials',
          title: 'Add accessorials',
          component: FootprintManager_accessorial_tasks_gridComponent,
          inParamsFunc: ($index?: number) => {
            return {
              workOrderId: null,
              projectId: $wizard.vars.projectId,
              entityStatusId: 1,
              orderId: $wizard.vars.orderId,
              warehouseId: $wizard.vars.warehouseId,
              shippingContainerId: $wizard.inParams.shippingContainerId,
              showAllOpCodes: null,
              shipmentId: null,
            }
          },
          next: 'step7_complete_verification',
      },
      {
          id: 'step7_complete_verification',
          title: 'Complete verification',
          component: FootprintManager_pack_verification_completion_editorComponent,
          inParamsFunc: ($index?: number) => {
            return {
              shippingContainerId: $wizard.inParams.shippingContainerId,
              startVerificationTaskId: $wizard.inParams.startVerificationTaskId,
              instructions: $wizard.vars.instructions.map(i => ({entity: i.entity, code: i.code, instruction: i.instruction, isCompleted: i.isCompleted})),
              scannedValues: $wizard.vars.scannedValues.map(v => ({ scannedValue: v.scannedValue, lotId: v.lotId, packagingId: v.packagingId })),
              properties: {containerType: $wizard.vars.properties.containerType, dimensions: $wizard.vars.properties.dimensions, weight: $wizard.vars.properties.weight},
              targetLocationId: $wizard.inParams.targetLocationId,
              can_fail_verification: $wizard.vars.can_fail,
            }
          },
          nextButtonLabel: 'Complete',
          nextButtonDisabledConditionFunc: ($index?: number) => {
            return $wizard.vars.is_existing_task;
          },
      },
      ];
    }

    return this._stepConfigs;
  }

  async finish() {
    const $wizard = this;
    const $utils = this.$utils;

    await this.on_finish();

    this.close();
  }

  close() {
    this.$finish.emit();
  }

  commandsTmpRefChange(tmp: any) {
    this.$commandsTmpRef.emit(tmp);
  }
 
  //#region private flows
  on_init(event = null) {
    return this.on_initInternal(
      this,
  this.$shell,
      this.$datasources,
      this.$flows,
      this.$reports,
      this.$settings,
      this.$operations,
      this.$utils,
      this.$context,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_initInternal(
    $wizard: FootprintManager_pack_verification_wizardComponent,
  
    $shell: FootprintManager_ShellService,
    $datasources: FootprintManager_DatasourceService,
    $flows: FootprintManager_FlowService,
    $reports: FootprintManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootprintManager_OperationService,
    $utils: UtilsService,
    $context: FootprintManager_pack_verification_wizard_ComponentContextService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootprintManager_LocalizationService,
    $event: any
  ) {
  
  $wizard.vars.isFailed = false;
  $wizard.vars.instructions = undefined;
  $wizard.vars.scannedValues = [];
  $wizard.vars.processQueue = [];
  $wizard.vars.is_properties_initialized = false;
  
  await get_configurations();
  
  $wizard.inParams.isBlind = $wizard.inParams.isBlind ?? true;
  try {
  
  
      if (await $operations.FootprintManager.Disable_Accessorials_ChargeEntry.isAuthorized()) {
          $wizard.vars.canAddAccessorials = false;
      } else {
          $wizard.vars.canAddAccessorials = true;
      }
  
      // Validate target location
      const shippingContainer = (await $datasources.PackVerification.ds_get_shippingcontainer_by_shippingContainerId.get({ shippingContainerId: $wizard.inParams.shippingContainerId })).result;
  
      const isContainerInInventory = shippingContainer.LicensePlates?.some(lp => !lp.Archived);;
      $wizard.vars.can_fail = isContainerInInventory;
  
      if (!$utils.isDefined($wizard.inParams.targetLocationId)) {
          // Get shipping container's current location
          $wizard.inParams.targetLocationId = shippingContainer.LicensePlates[0]?.LocationId;
      } else {
          const location = (await $datasources.Locations.ds_get_location_by_locationId.get({ locationId: $wizard.inParams.targetLocationId })).result[0];
  
          if (shippingContainer.LicensePlates[0]?.WarehouseId !== location.WarehouseId) {
              throw new Error(`Shipping container '${shippingContainer.LookupCode}' belongs to a different warehouse than the location '${location.Name}'`)
          }
      }
  
      let locationValidationResult = (await $flows.PackVerification.is_pack_verification_location_valid_flow({ shippingContainerId: $wizard.inParams.shippingContainerId, locationId: $wizard.inParams.targetLocationId }));
  
      if ($utils.isDefined(locationValidationResult.reasons)) {
          throw new Error((await $flows.Utilities.grammar_format_string_array_flow({ values: locationValidationResult.reasons })).formattedValue);
      }
  
      $wizard.vars.properties = { containerType: '', dimensions: '', weight: '' };
  
      // Get shipping container details
      $wizard.vars.orderId = shippingContainer.Shipment?.OrderLookups[0]?.Order?.Id;
      $wizard.vars.projectId = shippingContainer.Shipment?.OrderLookups[0]?.Order?.Project?.Id;
      $wizard.vars.warehouseId = shippingContainer.Shipment?.ActualWarehouseId;
      $wizard.vars.isContentValidated = false;
      $wizard.vars.isPropertiesValidated = false;
  
      // Get startVerificationTask
      let result = (await $flows.PackVerification.begin_pack_verification_flow({
          shippingContainerId: shippingContainer.Id,
          canUseExisting: $wizard.vars.configurations.canContinueClosedSessions
      }));
  
      if ($utils.isDefined(result.reasons)) {
          throw new Error((await $flows.Utilities.grammar_format_string_array_flow({ values: result.reasons })).formattedValue)
      }
  
      // Get task, if completed, read only
      const task = (await $datasources.PackVerification.ds_get_start_pack_verification_task_by_taskId.get({ taskId: result.startEndVerificationTaskId })).result;
      if (task.StatusId === 2) {
          $wizard.vars.isContentValidated = true;
          $wizard.vars.isPropertiesValidated = true;
          $wizard.vars.is_existing_task = true;
      }
  
      $wizard.inParams.startVerificationTaskId = result.startEndVerificationTaskId;
  
      add_events();
  
  }
  catch (error) {
      let targetError = error;
      while ($utils.isDefined(targetError.error)) {
          targetError = targetError.error;
      }
  
      await $shell.FootprintManager.openErrorDialog('Error initializing wizard', targetError.message);
  
      // @ts-ignore
      $wizard.close();
  }
  
  
  /************************************************
   * EVENTS
  *************************************************/
  async function add_events() {
      await new Promise(result => setTimeout(result, 100));
  
      // Get element to attach to 
      // const elements = document.querySelectorAll('h1.modal-title');
      // const element = Array.from(elements).find(e => e.textContent.trim().toUpperCase() === "PACK VERIFICATION") as HTMLElement;
      const element = document.getElementsByTagName('FootprintManager-pack_verification_wizard')[0];
  
      if (element == null) {
          // throw new Error(`Failed to identify the HTML element for pack verification wizard`);
      }
  
      // 'on' events to detect different stages of the wizard
      element.addEventListener('on_pack_verification_verify_container_editor_initialized', ((event: CustomEvent) => { }) as EventListener, { once: true });
  
      element.addEventListener('on_pack_verification_shipping_container_properties_editor_initialized', ((event: CustomEvent) => {
          if (!$wizard.vars.isContentValidated) { validateContents(); }
          set_properties_initialized();
      }) as EventListener, { once: true });
  
      element.addEventListener('on_accessorial_tasks_grid_initialized', ((event: CustomEvent) => {
          if (!$wizard.vars.isContentValidated) { validateContents(); }
          if (!$wizard.vars.isPropertiesValidated) { validateProperties(); }
      }) as EventListener, { once: true });
  
      element.addEventListener('on_pack_verification_completion_editor_initialized', ((event: CustomEvent) => {
          if (!$wizard.vars.isContentValidated) { validateContents(); }
          if (!$wizard.vars.isPropertiesValidated) { validateProperties(); }
      }) as EventListener, { once: true });
  
      // 'handle' events to perform common actions that may be invoked
      element.addEventListener('handle_scanned_values_changed', ((event: CustomEvent<{ scannedValue: string, materialId: number, serialNumberId: number, isVerified: boolean, reasons: string[], lotId: number, packagingId: number }[]>) => {
          $wizard.vars.scannedValues = event.detail;
      }) as EventListener);
  
      element.addEventListener('handle_container_instructions_changed', ((event: CustomEvent<{ entity: string, code: string, instruction: string, isCompleted: boolean, id: number }[]>) => {
          $wizard.vars.instructions = event.detail;
      }) as EventListener);
  
      element.addEventListener('handle_container_properties_changed', ((event: CustomEvent<{ containerType: string, dimensions: string, weight: string, actualWeight: number }>) => {
          $wizard.vars.properties = event.detail;
      }) as EventListener);
  
      element.addEventListener('handleCloseWizard', ((event: CustomEvent) => {
          // @ts-ignore
          $wizard.close();
      }) as EventListener, { once: true });
  }
  
  /************************************************
   * FUNCTIONS
  *************************************************/
  async function validateContents() {
      $wizard.vars.isContentValidated = true;
  
      let id = $utils.createGuid();
      $wizard.vars.processQueue.push(id);
  
      await new Promise(resolve => setTimeout(resolve, 100));
  
      if (id !== $wizard.vars.processQueue[0]) {
          $wizard.vars.processQueue.splice($wizard.vars.processQueue.indexOf(id), 1);
          return;
      }
      $wizard.vars.processQueue.splice($wizard.vars.processQueue.indexOf(id), 1);
  
  
      let result = (await $flows.PackVerification.validate_shipping_container_contents_flow({ startPackVerificationTaskId: $wizard.inParams.startVerificationTaskId, canSubstituteSerials: true }));
  
      if ($wizard.vars.scannedValues.find(v => !v.isVerified)) {
          failValidation('ValidateContents', ['There were invalid scans']);
      }
      else if (!result.isValid) {
          failValidation('ValidateContents', result.reasons);
      }
      else {
          document.getElementsByTagName('FootprintManager-pack_verification_wizard')[0].dispatchEvent(new CustomEvent('handlepack_verification_verify_container_editorValidated'));
      }
  }
  
  async function validateProperties() {
      $wizard.vars.isPropertiesValidated = true;
  
      let id = $utils.createGuid();
      $wizard.vars.processQueue.push(id);
  
      await new Promise(resolve => setTimeout(resolve, 100));
  
      if (id !== $wizard.vars.processQueue[0]) {
          $wizard.vars.processQueue.splice($wizard.vars.processQueue.indexOf(id), 1);
          return;
      }
      $wizard.vars.processQueue.splice($wizard.vars.processQueue.indexOf(id), 1);
  
      let requireWeight = true;
      let weightToValidate = $wizard.vars.properties.actualWeight;
  
      if (!$wizard.inParams.isBlind) {
          requireWeight = false;
          weightToValidate = null;
      }
  
      let result = (await $flows.PackVerification.validate_shipping_container_properties_flow({
          startPackVerificationTaskId: $wizard.inParams.startVerificationTaskId,
          additionalInputs: {
              isActualWeightRequired: requireWeight,
              actualWeight: weightToValidate
          }
      }));
  
      if (!result.isValid) {
          failValidation('ValidateProperties', result.reasons);
      }
      else {
          document.getElementsByTagName('FootprintManager-pack_verification_wizard')[0].dispatchEvent(new CustomEvent('handle_pack_verification_shipping_container_properties_editor_validated'));
      }
  
  }
  
  async function failValidation(context: string, reasons: string[]) {
      //await stagger();
  
      if (!$wizard.vars.isFailed) {
          $wizard.vars.isFailed = true;
  
          // Remove duplicates
          reasons = [... new Set(reasons)];
  
          // Get reason code 
          let reasonCodeId = (await $flows.PackVerification.get_failure_reasoncode_flow({ inputs: { context: context, taskId: $wizard.inParams.startVerificationTaskId } })).reasonCodeId;
  
          // Get target location to move shipping container to
          let targetLocationId = (await $flows.PackVerification.get_failure_location_flow({
              inputs: {
                  context: context,
                  defaultLocationId: $wizard.inParams.targetLocationId,
                  taskId: $wizard.inParams.startVerificationTaskId
              }
          })).targetLocationId;
  
          // Fail validation, close wizard
          $flows.PackVerification.fail_pack_verification_flow({
              shippingContainerId: $wizard.inParams.shippingContainerId,
              startPackVerificationTaskId: $wizard.inParams.startVerificationTaskId,
              targetLocationId: targetLocationId,
              reasonCodeId: reasonCodeId
          });
  
          await $shell.FootprintManager.openInfoDialog('Pack verification failed', `Verification failed because ${(await $flows.Utilities.grammar_format_string_array_flow({ values: reasons })).formattedValue}.`);
  
          // @ts-ignore
          $wizard.close();
      }
  }
  
  async function stagger() {
      await new Promise(resolve => setTimeout(resolve, Math.random() * 100));
  }
  
  async function get_configurations() {
      const CONFIGS = (await $flows.PackVerification.get_configurations_flow({})).configurations;
  
  
      $wizard.vars.configurations = {};
  
      if ($utils.isDefined(CONFIGS)) {
          $wizard.vars.configurations.canContinueClosedSessions = CONFIGS.can_continue_closed_sessions;
          $wizard.vars.configurations.requireCompleteAllInstructions = CONFIGS.require_complete_all_instructions;
          $wizard.vars.configurations.requireContainerType = CONFIGS.require_container_type;
          $wizard.vars.configurations.weightDiscrepancyThresholdPercent = CONFIGS.weight_variance_threshold_percent;
          $wizard.vars.configurations.autoPrintReportsOnComplete = CONFIGS.auto_print_reports_on_complete;
      }
  }
  
  async function set_properties_initialized() {
      await new Promise(resolve => setTimeout(resolve, 1000));
      $wizard.vars.is_properties_initialized = true;
  }
  }
  on_finish(event = null) {
    return this.on_finishInternal(
      this,
  this.$shell,
      this.$datasources,
      this.$flows,
      this.$reports,
      this.$settings,
      this.$operations,
      this.$utils,
      this.$context,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_finishInternal(
    $wizard: FootprintManager_pack_verification_wizardComponent,
  
    $shell: FootprintManager_ShellService,
    $datasources: FootprintManager_DatasourceService,
    $flows: FootprintManager_FlowService,
    $reports: FootprintManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootprintManager_OperationService,
    $utils: UtilsService,
    $context: FootprintManager_pack_verification_wizard_ComponentContextService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootprintManager_LocalizationService,
    $event: any
  ) {
  let reasons = (await $flows.PackVerification.complete_pack_verification_flow({
      startPackVerificationTaskId: $wizard.inParams.startVerificationTaskId,
      targetLocationId: $wizard.inParams.targetLocationId
  })).reasons;
  
  if ($utils.isDefined(reasons)) {
      let message = (await $flows.Utilities.grammar_format_string_array_flow({ values: reasons })).formattedValue;
      await $shell.FootprintManager.openErrorDialog('Error completing pack verification', message);
      throw new Error(message);
  }
  
  
  if ($wizard.vars.configurations.autoPrintReportsOnComplete && $utils.isDefinedTrimmed($settings.FootprintManager.PrintNodeKey) && $settings.FootprintManager.PrintNodeKey.trim().toLowerCase().replace('/', '') !== 'na') {
      const printResult = await $flows.FootprintManager.print_pack_verification_reports({ shipping_container_id: $wizard.inParams.shippingContainerId, location_id: $wizard.inParams.targetLocationId });
  
      if ($utils.isDefined(printResult.reasons)) {
          $shell.FootprintManager.openToaster(`Error printing packing lists`, printResult.reasons.join(', '), EToasterType.Error, { positionClass: EToasterPosition.bottomRight, timeOut: 10000, closeButton: true });
      } else {
          $shell.FootprintManager.openToaster(`Successfully printed packing reports`, null, EToasterType.Success, { positionClass: EToasterPosition.bottomRight, timeOut: 5000 });
      }
  }
  }
  //#endregion private flows
}
